import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useCardTemplate, CardTemplateField } from '@/hooks/useCardTemplates';
import { CardBuilderForm } from '@/components/card-builder/CardBuilderForm';
import { PhotoUpload } from '@/components/card-builder/PhotoUpload';
import { SignatureModal } from '@/components/card-builder/SignatureModal';
import { EditableCardPreview } from '@/components/card-builder/EditableCardPreview';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Edit3, Camera, PenLine, Loader2, RotateCcw } from 'lucide-react';
import type { CardCustomization } from '@/stores/cartStore';
import { calculatePhotoAspect, calculatePhotoOutputSize } from '@/lib/coordinateTransform';
import { generateCardThumbnail } from '@/lib/cardThumbnailGenerator';

interface CartEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  customization: CardCustomization;
  onSave: (customization: CardCustomization) => void;
}

// Check if a field is on the back side
const isBackSideField = (field: CardTemplateField): boolean => {
  return field.position_side === 'back';
};

export const CartEditModal: React.FC<CartEditModalProps> = ({
  isOpen,
  onClose,
  productId,
  productName,
  customization,
  onSave,
}) => {
  const [formData, setFormData] = useState<Record<string, string>>(customization.formData);
  const [photo, setPhoto] = useState<string | null>(customization.photo);
  const [signatureFont, setSignatureFont] = useState(customization.signatureFont);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const [focusedFieldKey, setFocusedFieldKey] = useState<string | null>(null);
  
  // Image dimensions for calculating correct photo crop aspect ratio
  const [imageDims, setImageDims] = useState<{ width: number; height: number } | null>(null);

  // Fetch template from database
  const { data: template, isLoading } = useCardTemplate(productId);
  
  // Auto-flip card when user focuses on a back-side field
  useEffect(() => {
    if (!focusedFieldKey || !template?.fields) return;
    
    const field = template.fields.find(f => f.field_key === focusedFieldKey);
    if (field && isBackSideField(field)) {
      setShowBack(true);
    } else if (field) {
      setShowBack(false);
    }
  }, [focusedFieldKey, template?.fields]);
  
  // Load front image dimensions to calculate correct photo crop aspect ratio
  useEffect(() => {
    if (!template?.front_image_url) {
      setImageDims(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      setImageDims({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => setImageDims(null);
    img.src = template.front_image_url;
  }, [template?.front_image_url]);
  
  // Calculate photo aspect ratio using coordinate transformation
  // This correctly maps container-space coordinates to image-space
  const photoAspect = useMemo(() => {
    if (!imageDims || !template?.photo_position) return undefined;
    return calculatePhotoAspect(
      imageDims.width,
      imageDims.height,
      template.photo_position as { width: number; height: number }
    );
  }, [imageDims, template?.photo_position]);
  
  const photoOutputSize = useMemo(() => {
    if (!imageDims || !template?.photo_position) return undefined;
    return calculatePhotoOutputSize(
      imageDims.width,
      imageDims.height,
      template.photo_position as { width: number; height: number }
    );
  }, [imageDims, template?.photo_position]);

  // Auto-generate signature from name
  const signatureText = useMemo(() => {
    const firstName = formData.firstName || formData.first_name || '';
    const lastName = formData.lastName || formData.last_name || '';
    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}. ${lastName}`;
    }
    return '';
  }, [formData]);

  const handleFieldChange = (fieldKey: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSave = useCallback(async () => {
    // Regenerate thumbnail with current photo
    let cardPreviewDataUrl: string | undefined;
    if (template) {
      const templateImgUrl = template.thumbnail_front_url || template.front_image_url;
      const photoPos = template.photo_position as { x: number; y: number; width: number; height: number } | null;
      const thumb = await generateCardThumbnail(templateImgUrl, photo, photoPos);
      cardPreviewDataUrl = thumb || undefined;
    }

    onSave({
      formData,
      photo,
      signatureText,
      signatureFont,
      cardPreviewDataUrl,
    });
    onClose();
  }, [template, formData, photo, signatureText, signatureFont, onSave, onClose]);

  if (!template || isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-primary" />
              Edit {productName}
            </DialogTitle>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-6 py-4">
            {/* Preview */}
            <div className="space-y-4">
              <EditableCardPreview
                template={template}
                formData={formData}
                photo={photo}
                signatureText={signatureText}
                signatureFont={signatureFont}
                showBack={showBack}
                onFieldChange={handleFieldChange}
                onPhotoClick={() => setShowPhotoUpload(true)}
                onSignatureClick={() => setShowSignatureModal(true)}
              />

              {/* Quick Actions */}
              <div className="flex gap-2 justify-center flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBack(!showBack)}
                  className="gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  {showBack ? 'Vorderseite' : 'Rückseite'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPhotoUpload(true)}
                  className="gap-2"
                >
                  <Camera className="h-4 w-4" />
                  Photo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSignatureModal(true)}
                  className="gap-2"
                >
                  <PenLine className="h-4 w-4" />
                  Signature
                </Button>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-4">
              {showPhotoUpload && (
                <>
                  <PhotoUpload 
                    photo={photo} 
                    onPhotoChange={setPhoto}
                    aspect={photoAspect}
                    outputSize={photoOutputSize}
                  />
                  <Separator />
                </>
              )}

              <CardBuilderForm
                template={template}
                formData={formData}
                onFieldChange={handleFieldChange}
                onFieldFocus={setFocusedFieldKey}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        signatureText={signatureText}
        selectedFont={signatureFont}
        onFontChange={setSignatureFont}
      />
    </>
  );
};
